import cn from "classnames";
import dayjs, { Dayjs } from "dayjs";
import { CountryCode } from "libphonenumber-js";

import AirlineStopsOutlinedIcon from '@mui/icons-material/AirlineStopsOutlined';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import AirlinesRoundedIcon from '@mui/icons-material/AirlinesRounded';

import { PRO_ALERT_LIMIT } from "@/constants/membership";
import GradeRoundedIcon from '@mui/icons-material/GradeRounded';
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import FlightLandOutlinedIcon from "@mui/icons-material/FlightLandOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  Box,
  Button,
  Checkbox,
  Fab,
  FormControlLabel,
  Grid,
  IconButton,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { createAlertAction, getUsageAction, modifyAlertAction } from "@/api/alert";
import ProChip from "@/components/ProChip";
import SearchRangeDate from "@/components/SearchBar/SearchRangeDate";
import SearchSelect from "@/components/SearchBar/SearchSelect";
import { AlertTypeEnum, cabinOptionMap } from "@/constants";
import { AREA_CODES } from "@/constants/areaCode";
import { MAX_ROUTE_CITY_COUNT } from "@/constants/config";
import { ROUTES_STOPS_OPTIONS } from "@/constants/filters";
import { BANK_NAME_MAP, PROGRAM_NAME_MAP, tranfer_bank_to_program } from "@/constants/programs";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useEvent from "@/hooks/useEvent";
import usePlatform from "@/hooks/usePlatform";
import useSnackbar from "@/hooks/useSnackbar";
import useThemeData from "@/hooks/useThemeData";
import SelectLocation from "@/pages/Routes/components/SelectLocation";
import { transformerOptions } from "@/utils";
import { validatePhoneNumber } from "@/utils/validatePhone";

import { useAlertContext } from "./AlertContext";
import FormItem from "./FormItem";
import FormPhoneItem, { validatePhone } from "./FormPhoneItem";
import Programs from "./Programs";
import useCheckCondition from "./hooks/useCheckCondition";
import useCondition, { createSearchQuery, parseSearchQuery } from "./hooks/useCondition";

interface Option {
  label: string;
  value: string;
}

interface FromData {
  alert_type: string;
  alertId?: string;
  cabins: string;
  fromLocation: string[];
  toLocation: string[];
  departureDate: number;
  departureDateSec: number;
  stops: string;
  banks: Option[];
  airlines: Option[];
  maxPoints: string;
  valid: boolean;
  areaCode: string;
  phoneNumber: string;
  infiniteMode: boolean;
  agreeReceiveSMS: boolean;
}

export interface IRef {
  show: (data: FromData) => void;
  submit: () => void;
}

interface PanoramaFormProps {
  onSuccess?: () => void;
  onReset?: () => void;
}

const allOption = { label: "All", value: "" };

const PanoramaForm = forwardRef<IRef, PanoramaFormProps>((props, ref) => {
  const theme = useThemeData();
  const { isMobile } = usePlatform();
  const { enqueueSnackbar } = useSnackbar();

  const {
    setLoading,
    open,
    setOpen,
    setOpenResult,
    setAlertModalInfo,
    infiniteMode,
    setInfiniteMode,
  } = useAlertContext();

  const [initDataUsedAlerts, setInitDataUsedAlerts] = useState(0);
  const [usageLoading, setUsageLoading] = useState(true);
  const [visible, setVisible] = useState(false);

  const { checkCondition } = useCheckCondition();

  const [alertNeeded, setAlertNeeded] = useState(-1);
  const [alertUsageInfo, setAlertUsageInfo] = useState({
    alertLimit: 0,
    alertUsed: 0,
    alertLeft: 0,
  });

  const store = useGlobalStoreContext();
  const navigate = useNavigate();

  const bankOptions = useMemo(() => {
    return Object.keys(BANK_NAME_MAP).map((key) => ({ value: key, label: BANK_NAME_MAP[key] }));
  }, []);

  const airlineOptions = useMemo(() => {
    return transformerOptions(PROGRAM_NAME_MAP).map((option) => ({
      value: option.value,
      label: option.label,
    }));
  }, []);

  const defaultFormData = useRef({
    alert_type: "panorama",
    alertId: "",
    // passengers: passengerTypeOptions[0].value,
    cabins: cabinOptionMap[0].value,
    fromLocation: [],
    toLocation: [],
    departureDate: 0,
    departureDateSec: 0,
    stops: ROUTES_STOPS_OPTIONS[0].value,
    banks: [allOption],
    airlines: [allOption],
    maxPoints: "",
    // mixed_cabin: 80,
    // flightNumber: "",
    // maxTax: "",
    // departureTime: null,
    // arrivalTime: null,
    // infiniteMode: false,
    valid: true,
    areaCode: AREA_CODES[0].dial_code,
    phoneNumber: "",
    agreeReceiveSMS: false,
  });

  const [formData, setFormData] = useState<FromData>(defaultFormData.current);

  const { condition, setCondition, onLocationSwapChange } = useCondition({
    fromLocation: formData.fromLocation,
    toLocation: formData.toLocation,
  });

  const disableSwap = useMemo(() => {
    return !condition.fromLocation.length || !condition.toLocation.length;
  }, [condition.fromLocation, condition.toLocation]);

  const alertsInfo = useMemo(() => {
    let text = "";
    let color = theme.palette.success.main;
    let icon = <NotificationsActiveIcon sx={{ color }} fontSize="small" />;

    const { alertLeft } = alertUsageInfo;

    if (alertNeeded >= 0) {
      text += `${alertNeeded} active alerts needed, `;
    }

    if (alertLeft >= 0) {
      text += `${alertLeft} active alerts left`;
    }

    if (alertNeeded >= 0 && alertLeft >= 0) {
      if (alertNeeded > alertLeft) {
        color = theme.palette.error.main;
        icon = <ErrorOutlinedIcon sx={{ color }} fontSize="small" />;
      } else {
        icon = <CheckCircleOutlineIcon sx={{ color }} fontSize="small" />;
      }
    }

    return { text, color, icon };
  }, [alertUsageInfo, alertNeeded, theme.palette]);

  const handleCreate = useEvent(async () => {
    const maxPointsStr = (formData.maxPoints || "").trim();

    let message = "";

    const errMsg = checkCondition(condition);

    if (errMsg) {
      return;
    }

    if (!maxPointsStr) {
      message = "Max Points per Passenger cannot be empty.";
    } else if (isNaN(Number(maxPointsStr))) {
      message = "Max Points per Passenger need to be a number.";
    }

    if (formData.phoneNumber && formData.areaCode) {
      const valid = validatePhone(formData);

      if (!valid) {
        message = "Invalid phone number.";
      }

      if (!formData.agreeReceiveSMS) {
        message = "Please agree to receive SMS based on your data";
      }
    }

    if (message) {
      enqueueSnackbar(message);
      return;
    }

    try {
      const conditionParams = createSearchQuery(condition);
      const params: any = {
        filter_criteria: {
          cabins: formData.cabins.split("&").map((item) => item.trim()),
          banks: formData.banks.map((ele) => ele.value || "All"),
          airline_programs: formData.airlines.map((ele) => ele.value || "All"),
          max_points: Number(formData.maxPoints.trim()),
          stops: formData.stops,
        },
        info: {
          passengers: 1,
          ...conditionParams,
          departureDate: dayjs(formData.departureDate).format("YYYY-MM-DD"),
          departureDateSec: dayjs(formData.departureDateSec).format("YYYY-MM-DD"),
        },
        alert_type: AlertTypeEnum.Panorama,
        infinite_mode: infiniteMode,
        phone_number: {
          number: formData.phoneNumber,
          area: formData.areaCode,
          agreeReceiveSMS: formData.agreeReceiveSMS,
        },
      };

      setLoading(true);

      if (formData.alertId) {
        await modifyAlertAction({ ...params, alert_id: formData.alertId });

        props.onSuccess && props.onSuccess();
        setOpen(false);
      } else {
        await createAlertAction(params);

        setAlertModalInfo({
          alertType: AlertTypeEnum.Panorama,
          isSuccess: true,
          fromLocation: condition.fromLocation,
          toLocation: condition.toLocation,
          departureDate: formData.departureDate,
          departureDateSec: formData.departureDateSec,
        });

        props.onSuccess && props.onSuccess();

        setOpen(false);
        setOpenResult(true);
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.indexOf("Exceed alert limit") !== -1) {
          setAlertModalInfo({
            isSuccess: false,
            alertLimit: alertUsageInfo.alertLimit,
          });

          setOpen(false);
          setOpenResult(true);
        } else {
          enqueueSnackbar(error.message || "");
        }
      }
    } finally {
      setLoading(false);
    }
  });

  const handleDepartureDateChange = useEvent((date: Array<Dayjs | null>) => {
    setFormData({
      ...formData,
      departureDate: date[0] ? dayjs(date[0]).valueOf() : 0,
      departureDateSec: date[1] ? dayjs(date[1]).valueOf() : 0,
    });
  });

  const handleBankChange = useEvent((data: Option[]) => {
    let nextAirline = [...formData.airlines];

    if (data.length) {
      let programs: string[] = [];

      data.forEach((bank) => {
        programs = programs.concat(tranfer_bank_to_program.get(bank.label) || []);
      });

      programs = [...new Set(programs)];

      if (programs.length) {
        programs = [...programs, ...formData.airlines.map((ele) => ele.value)];
        programs = [...new Set(programs)];
        nextAirline = airlineOptions.filter((ele) => programs.includes(ele.value));
      }
    }

    setFormData({
      ...formData,
      banks: data.length === 0 ? [allOption] : data,
      airlines: nextAirline,
    });
  });

  const handleAirlineChange = useEvent((data: Option[]) => {
    setFormData({
      ...formData,
      airlines: data.length === 0 ? [allOption] : data,
    });
  });

  const promptUpsellUI = () => {
    return store?.show_upsell ? (
      <Typography
        component={Button}
        variant="body2"
        color="primary"
        onClick={() => {
          navigate("/pro");
        }}
        sx={{ marginTop: "-5px", paddingLeft: "10px", marginLeft: "10px", lineHeight: "16px" }}
      >
        <div className="mr-[6px]">
          <ProChip />
        </div>
        Get {PRO_ALERT_LIMIT} alerts now!
      </Typography>
    ) : null;
  };

  const getUsage = useEvent(() => {
    setUsageLoading(true);
    getUsageAction()
      .then((res) => {
        const alertLeft = res.max_alert - res.alert_set;

        setAlertUsageInfo({
          alertLimit: res.max_alert,
          alertUsed: res.alert_set,
          alertLeft: alertLeft + initDataUsedAlerts,
        });
        setUsageLoading(false);
      })
      .catch(() => {
        setUsageLoading(false);
      });
  });

  const handleShow = (data: FromData) => {
    if (data) {
      const newData = {
        ...defaultFormData.current,
        ...(data || {}),
      };

      setFormData(newData);

      setInfiniteMode(data.infiniteMode || false);

      const t = parseSearchQuery({
        fromLocation: data.fromLocation,
        toLocation: data.toLocation,
      });

      setCondition(t);

      if (data.alertId && data.valid) {
        const usedAlerts =
          (dayjs(data.departureDateSec).startOf("day").diff(data.departureDate, "day") + 1) *
          data.toLocation.length *
          data.fromLocation.length;

        setInitDataUsedAlerts(usedAlerts);
      }
      setOpen(true);
    }
  };

  const handleClose = useEvent(() => {
    props.onReset && props.onReset();
  });

  useEffect(() => {
    if (open) {
      getUsage();
    } else {
      setTimeout(() => {
        setFormData(defaultFormData.current);
        setAlertUsageInfo({ alertLimit: 0, alertUsed: 0, alertLeft: -1 });
        setAlertNeeded(-1);
        setInitDataUsedAlerts(0);
        handleClose();
      }, 500);
    }
  }, [open, getUsage, handleClose]);

  useEffect(() => {
    if (
      formData.departureDate &&
      formData.departureDateSec &&
      formData.fromLocation.length &&
      formData.toLocation.length
    ) {
      const count =
        (dayjs(formData.departureDateSec).startOf("day").diff(formData.departureDate, "day") + 1) *
        formData.fromLocation.length *
        formData.toLocation.length;

      setAlertNeeded(count);
    } else {
      setAlertNeeded(-1);
    }
  }, [
    formData.departureDate,
    formData.departureDateSec,
    formData.fromLocation,
    formData.toLocation,
  ]);

  const renderContent = () => {
    return (
      <>
        <Box className="flex items-center mb-[10px]">
          <SearchSelect
            value={formData.cabins}
            options={cabinOptionMap}
            onChange={(data) => {
              setFormData({ ...formData, cabins: data });
            }}
          />
        </Box>

        <Grid container rowSpacing={2} columnSpacing={1} className="relative">
          <Grid item xs={12}>
            <Grid container alignItems="center" rowGap={2}>
              <Grid item xs={12} sm={5.6}>
                <SelectLocation
                  maxCount={MAX_ROUTE_CITY_COUNT}
                  placeholder="Where from?"
                  Icon={<FlightTakeoffOutlinedIcon />}
                  key={condition.fromLocation.length}
                  selectData={condition.fromLocation}
                  onChange={(data) => {
                    setCondition({ ...condition, fromLocation: data });
                  }}
                />
              </Grid>

              {!isMobile ? (
                <Grid item xs={1} sm={0.8} alignItems="center">
                  <div className="flex items-center justify-center">
                    <IconButton
                      aria-label="swap"
                      size="small"
                      onClick={onLocationSwapChange}
                      disabled={disableSwap}
                    >
                      <SwapHorizIcon />
                    </IconButton>
                  </div>
                </Grid>
              ) : (
                <div className={cn("absolute right-6 z-10", { hidden: disableSwap })}>
                  <Fab
                    color="primary"
                    aria-label="swap"
                    size="small"
                    onClick={onLocationSwapChange}
                  >
                    <SwapHorizIcon className="rotate-90" />
                  </Fab>
                </div>
              )}

              <Grid item xs={12} sm={5.6}>
                <SelectLocation
                  maxCount={MAX_ROUTE_CITY_COUNT}
                  placeholder="Where to?"
                  Icon={<FlightLandOutlinedIcon />}
                  selectData={condition.toLocation}
                  key={condition.toLocation.length}
                  onChange={(data) => {
                    setCondition({ ...condition, toLocation: data });
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box className="mt-[16px]">
          <SearchRangeDate
            label=""
            defaultValue={[
              formData.departureDate ? dayjs(formData.departureDate) : null,
              formData.departureDateSec ? dayjs(formData.departureDateSec) : null,
            ]}
            onChange={handleDepartureDateChange}
          />
        </Box>

        {!usageLoading && alertsInfo.text ? (
          <Box className="mb-[6px] mt-[16px] flex items-center">
            {alertsInfo.icon}
            <Typography
              variant="body2"
              sx={{ color: alertsInfo.color, paddingLeft: "5px", lineHeight: "16px" }}
            >
              {alertsInfo.text}
            </Typography>
            {!isMobile ? promptUpsellUI() : null}
          </Box>
        ) : (
          <div className="h-[16px]" />
        )}
        {isMobile ? promptUpsellUI() : null}
        {/* <FormPhoneItem
          formData={formData}
          show_upsell={store.show_upsell}
          setFormData={(value) => setFormData({ ...formData, ...value })}
        /> */}
        <FormItem label={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <GradeRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
            Max Points per Passenger *:
          </Box>
        }>
          <TextField
            placeholder="Required"
            variant="outlined"
            size="small"
            type="number"
            value={formData.maxPoints}
            onChange={(e) => {
              setFormData({ ...formData, maxPoints: e.target.value });
            }}
            inputProps={{ style: { fontSize: "14px", width: "80px" }, min: 0 }}
          />
        </FormItem>
        <FormItem label={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AirlineStopsOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
            Stops:
          </Box>
        }>
          <SearchSelect
            value={formData.stops}
            options={ROUTES_STOPS_OPTIONS}
            onChange={(data) => {
              setFormData({ ...formData, stops: data });
            }}
          />
        </FormItem>

        <FormItem label={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CreditCardRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
            Card Programs:
          </Box>
        } labelClass="h-[48px] min-w-[110px] self-start">
          <Programs
            size="small"
            title="Bank Programs"
            options={bankOptions}
            selectOptions={formData.banks}
            onChange={handleBankChange}
            placeholder="Select Banks"
          />
        </FormItem>

        <FormItem label={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AirlinesRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
            Airline Programs:
          </Box>
        } labelClass="h-[48px] min-w-[110px] self-start">
          <Programs
            size="small"
            title="Airline Programs"
            options={airlineOptions}
            selectOptions={formData.airlines}
            onChange={handleAirlineChange}
            placeholder="Airlines"
          />
        </FormItem>
      </>
    );
  };

  useImperativeHandle(ref, () => ({
    show: handleShow,
    submit: handleCreate,
  }));

  return (
    <div>
      {isMobile ? renderContent() : renderContent()}
      <Modal open={visible} onClose={() => setVisible(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            p: 4,
            boxShadow: 2,
            borderRadius: "12px",
          }}
        >
          <ProChip />
          <Typography color="textPrimary">{"Feature - "}</Typography>
        </Box>
      </Modal>
    </div>
  );
});

PanoramaForm.displayName = "PanoramaForm";

export default PanoramaForm;
